import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html } from 'lit';
import { UsageAPI } from '../../api/UsageAPI.js';
import { renderDashboardCardHeader, renderLiveBadge, renderReadableInteger } from './StatRenderUtilities.js';

/**
 * The background queues that live as long as the server. The names are the queue names of the worker pools
 * (`MATERIALIZED_VIEW_QUEUE`, `ACCOUNTING_RESOLVE_QUEUE`, `ACCOUNTING_MATCHING_QUEUE`, `MICROSOFT_SYNC_QUEUE`,
 * `PERMISSION_SYNC_QUEUE`) — written out because those constants live in server files the client must not import.
 */
const QUEUES = [
  {
    queue: 'materializedViews',
    title: 'Materialisierte Ansichten',
    iconClass: 'icon-[material-symbols--pending-actions-outline-rounded]',
  },
  {
    queue: 'accounting-resolve',
    title: 'Buchhaltung · Zuordnung',
    iconClass: 'icon-[material-symbols--receipt-long-outline-rounded]',
  },
  {
    queue: 'accounting-matching',
    title: 'Buchhaltung · Vorgänge',
    iconClass: 'icon-[material-symbols--account-tree-outline-rounded]',
  },
  {
    queue: 'microsoft-sync',
    title: 'Microsoft-Abgleich',
    iconClass: 'icon-[material-symbols--cloud-sync-outline-rounded]',
  },
  {
    queue: 'permission-sync',
    title: 'Rollen & Personalbuchungen',
    iconClass: 'icon-[material-symbols--manage-accounts-outline-rounded]',
  },
];

/**
 * ponytail: the lane fills by clipping, not by measuring, so this only has to be wider than any lane — 200 blocks are
 * ~3200px. A wider lane would show a gap on the left; measure the lane with a ResizeObserver if that ever matters.
 */
const MAX_JOBS_DRAWN = 200;

/**
 * Literal class strings per state, so Tailwind finds them. Working is the normal case; jobs waiting while nothing runs
 * is the one to look at — a retry backing off, or a worker that is gone.
 */
const STATES = {
  working: {
    label: 'Arbeitet',
    job: 'bg-info/50',
    arrow: 'text-info',
    worker: 'border-info/50',
    workerIcon: 'icon-[material-symbols--progress-activity] animate-spin text-info',
  },
  waiting: {
    label: 'Wartet',
    job: 'bg-warning/70',
    arrow: 'text-base-content/40',
    worker: 'border-warning/60',
    workerIcon: 'icon-[material-symbols--hourglass-empty-outline-rounded] text-warning',
  },
  idle: {
    label: 'Bereit',
    job: '',
    arrow: 'text-base-content/40',
    worker: 'border-base-300',
    workerIcon: 'icon-[material-symbols--check-circle-outline-rounded] text-success',
  },
  unknown: {
    label: '–',
    job: '',
    arrow: 'text-base-content/40',
    worker: 'border-base-300',
    workerIcon: 'icon-[material-symbols--help-outline-rounded] text-base-content/40',
  },
};

/**
 * Each queue drawn as what it is: a line of waiting jobs flowing into its worker. Read-only on purpose: the queues
 * start, retry and drain by themselves — this card answers "is it moving, and is a backlog building up?", nothing more.
 */
export const JobQueuesSection = stateful(() => {
  // a fixed list, so the hooks run in the same order on every render
  const statuses = QUEUES.map(
    ({ queue }) => useQuery(() => UsageAPI.queueStatus(queue)({ queued: 1, running: 1 }), []).value,
  );

  return html`
    <div class="card h-full">
      <div class="card-body gap-2">
        ${renderDashboardCardHeader(
          'Hintergrundaufträge',
          'icon-[material-symbols--stacks-outline-rounded]',
          renderLiveBadge(),
          'Links die wartenden Aufträge, rechts der Worker, der sie abarbeitet.',
        )}

        <ul class="flex flex-col divide-y divide-base-300">
          ${QUEUES.map(({ title, iconClass }, index) => {
            const status = statuses[index];
            const queued = status?.queued ?? 0;
            const running = status?.running ?? 0;
            const state = !status ? STATES.unknown : running ? STATES.working : queued ? STATES.waiting : STATES.idle;
            const drawn = Math.min(queued, MAX_JOBS_DRAWN);

            return html`
              <li class="flex flex-col gap-3 py-3 md:flex-row md:items-center md:gap-6">
                <div class="flex min-w-0 items-center gap-3 md:w-72 md:shrink-0">
                  <span class="flex size-10 shrink-0 items-center justify-center rounded-field bg-base-200">
                    <span class=${`${iconClass} size-6 text-base-content/70`}></span>
                  </span>
                  <div class="min-w-0">
                    <p class="truncate font-semibold">${title}</p>
                    <p class="text-sm text-base-content/70">
                      ${
                        status
                          ? `${renderReadableInteger(queued)} wartend · ${renderReadableInteger(running)} in Arbeit`
                          : 'Keine Daten'
                      }
                    </p>
                  </div>
                </div>

                <div class="flex min-w-0 flex-1 items-center gap-2" aria-hidden="true">
                  <div class="flex h-9 min-w-0 flex-1 items-center rounded-field bg-base-200 px-2">
                    ${
                      drawn
                        ? html`
                            <!-- the head of the line sits next to the worker; what does not fit is cut off on the
                                 left, and the fade says the line goes on -->
                            <div class="flex min-w-0 flex-1 justify-end gap-1 overflow-hidden mask-l-from-90%">
                              ${Array.from(
                                { length: drawn },
                                (_, position) =>
                                  html`<span
                                    class=${`h-5 w-3 shrink-0 rounded-sm ${state.job} ${position === drawn - 1 && running ? 'animate-pulse' : ''}`}
                                  ></span>`,
                              )}
                            </div>
                          `
                        : html`<span class="mx-auto text-sm text-base-content/70">leer</span>`
                    }
                  </div>
                  <span class=${`icon-[material-symbols--arrow-forward-rounded] size-5 shrink-0 ${state.arrow}`}></span>
                  <div
                    class=${`flex h-9 w-36 shrink-0 items-center gap-2 rounded-field border bg-base-100 px-3 ${state.worker}`}
                  >
                    <span class=${`size-5 shrink-0 ${state.workerIcon}`}></span>
                    <span class="truncate text-sm font-semibold">
                      ${running ? `${renderReadableInteger(running)} in Arbeit` : state.label}
                    </span>
                  </div>
                </div>
              </li>
            `;
          })}
        </ul>
      </div>
    </div>
  `;
});
